import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { getPipelineReviewQueue } from '../../services/companyPipelineService';
import {
    getReviewQueueState,
    publishReviewQueueTotal,
    subscribeReviewQueue,
} from '../../utils/reviewQueueSignal';

/**
 * PipelineReviewGate
 *
 * Global, pre-render gate that forces a company user to make an explicit
 * decision on every negotiation that has gone stale before they can use the
 * rest of the app. Modelled on TosAcceptanceGate, which solves the same shape
 * of problem (a condition that must be satisfied before any authenticated page
 * mounts) and is already proven in production.
 *
 * Three deliberate differences from TosAcceptanceGate:
 *
 * 1. The condition comes from the review-queue endpoint, not `auth.user`.
 *    Putting a count on /auth/me would have made AuthModule depend on the
 *    matching module's pipeline service (a circular import).
 *
 * 2. The gate does NOT own the answer, `utils/reviewQueueSignal` does. The gate
 *    only fetches when nobody has asked the server yet this session; after
 *    that, the review page publishes what it learns. This is the fix for the
 *    redirect loop reported 2026-08-27: when the gate cached its own boolean,
 *    finishing the queue navigated to /company/pipeline while that boolean was
 *    still `true`, so the gate bounced the user back forever. See the header
 *    of reviewQueueSignal.js for why re-fetching cannot fix that and a
 *    published count can.
 *
 * 3. It FAILS OPEN. If the check cannot be loaded, the user gets into the app.
 *    Blocking someone out of their own product because a request failed is
 *    worse than missing a review prompt.
 *
 * Only GROUP users are ever gated: a provider has no company pipeline.
 */

const REVIEW_PATH = '/company/pipeline/review';

// Exempt paths. The review page itself, or the redirect loops. And /logout: a
// user must always be able to leave. A queue with no exit is a support
// incident, not a product.
const ALLOWED_PATHS = [REVIEW_PATH, '/logout', '/accept-terms'];

const PipelineReviewGate = ({ children }) => {
    const { auth } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();

    const [{ total, gateEnabled }, setQueueState] = useState(getReviewQueueState);

    const token = auth?.token;
    const isCompanyUser = auth?.user?.role === 'GROUP';

    // Whoever publishes (this gate on first load, the review page after every
    // decision) updates every subscriber synchronously.
    useEffect(() => subscribeReviewQueue(setQueueState), []);

    // Guard against a second fetch from a re-render while the first is still in
    // flight. NOT keyed on pathname: the answer is about the user, not the page,
    // and keying it on the path is what made the old version re-ask a question
    // it had already been told the answer to.
    const fetching = useRef(false);
    const onReviewPage = location.pathname.startsWith(REVIEW_PATH);
    // Skipping the check on the review page is an efficiency measure ONLY: the
    // page fetches the same queue and publishes the result, so a second request
    // would be redundant. It is NOT the gate declining to find out, which is
    // what caused the old redirect loop.
    const needsCheck = !!token && isCompanyUser && total === null && !onReviewPage;

    useEffect(() => {
        if (!needsCheck || fetching.current) return;
        fetching.current = true;

        let cancelled = false;
        (async () => {
            try {
                const queue = await getPipelineReviewQueue(token);
                if (!cancelled) publishReviewQueueTotal(queue.total, queue.gateEnabled);
            } catch {
                // FAIL OPEN. Publish zero so the app is usable and the gate does
                // not re-ask on every navigation; a reload re-checks.
                if (!cancelled) publishReviewQueueTotal(0);
            } finally {
                fetching.current = false;
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [needsCheck, token]);

    const pathAllowed = ALLOWED_PATHS.some((p) => location.pathname.startsWith(p));
    // `total === null` (not yet known) is explicitly NOT blocking: an unknown
    // must never hold the app hostage.
    const shouldBlock =
        isCompanyUser && gateEnabled && typeof total === 'number' && total > 0 && !pathAllowed;

    useEffect(() => {
        if (!shouldBlock) return;
        navigate(REVIEW_PATH, {
            replace: true,
            // Carry the destination so the queue can return the user where they
            // were headed once it is empty.
            state: { from: location.pathname + location.search },
        });
    }, [shouldBlock, navigate, location.pathname, location.search]);

    // Render nothing while a gated user sits on a blocked path, so the
    // underlying page never mounts and never fires its API calls.
    if (shouldBlock) return null;

    return children;
};

export default PipelineReviewGate;
